/** Three action boxes shown in the expanded header when no conversation is active. */
import { PenLine, Brain, Search } from 'lucide-react';

interface CommandBarActionsProps {
  onSelect: (mode: 'create' | 'revise' | 'search') => void;
}

const actions = [
  {
    mode: 'create' as const,
    icon: PenLine,
    title: 'Create',
    description: 'Generate a lesson on any topic',
    hint: 'Build from an idea',
    gradient: 'linear-gradient(135deg, #FF9B4A 0%, #FF6EB1 100%)',
    iconColor: '#FF8A3D',
  },
  {
    mode: 'revise' as const,
    icon: Brain,
    title: 'Revise',
    description: 'Practise your weakest areas',
    hint: 'Start a focused pass',
    gradient: 'linear-gradient(135deg, #9880C8 0%, #6C5CE7 100%)',
    iconColor: '#8A6CF0',
  },
  {
    mode: 'search' as const,
    icon: Search,
    title: 'Search',
    description: 'Find courses, lessons, and more',
    hint: 'Jump to content',
    gradient: 'linear-gradient(135deg, #4ECDC4 0%, #44A3E0 100%)',
    iconColor: '#44A3E0',
  },
] as const;

export function CommandBarActions({ onSelect }: CommandBarActionsProps) {
  return (
    <div className="mx-auto flex w-full flex-col gap-3 px-1 py-4" style={{ maxWidth: 820 }}>
      <div className="flex items-end justify-between gap-4 px-1">
        <div>
          <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
            Quick starts
          </p>
          <p
            className="mt-1"
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-snug)',
            }}
          >
            Choose a mode, or keep typing in the command bar.
          </p>
        </div>
      </div>

      <div className="grid w-full grid-cols-3 gap-3">
        {actions.map((action, i) => (
          <button
            key={action.mode}
            type="button"
            onClick={() => onSelect(action.mode)}
            className="group relative flex min-h-[136px] cursor-pointer flex-col justify-between overflow-hidden rounded-[1.45rem] p-4 text-left transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
            style={{
              background:
                'linear-gradient(145deg, var(--color-surface-solid) 0%, color-mix(in srgb, var(--color-accent-subtle) 28%, var(--color-surface-solid)) 100%)',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-sm)',
              animation: `cba-emerge 0.45s cubic-bezier(0.32, 0.72, 0, 1) ${0.12 + i * 0.07}s both`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-accent-outline)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
            }}
          >
            <span
              aria-hidden
              className="absolute -top-10 -right-10 h-24 w-24 rounded-full opacity-15 transition-opacity duration-200 group-hover:opacity-25"
              style={{ background: action.gradient }}
            />

            <div className="relative flex items-start justify-between gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-xl"
                style={{
                  background: action.gradient,
                  boxShadow: `0 2px 8px ${action.iconColor}33`,
                }}
              >
                <action.icon size={18} strokeWidth={2} style={{ color: '#fff' }} />
              </div>

              <span
                className="rounded-full px-2 py-1"
                style={{
                  background: 'var(--color-surface-solid)',
                  color: 'var(--color-text-muted)',
                  fontSize: '0.75rem',
                  fontWeight: 650,
                }}
              >
                {action.hint}
              </span>
            </div>

            <div className="relative mt-5">
              <p
                className="font-semibold leading-none"
                style={{
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                }}
              >
                {action.title}
              </p>
              <p
                className="mt-2 leading-snug"
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {action.description}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
